import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Lock, CheckCircle2, Mail, Sparkles, Loader2 } from "lucide-react";

const mailto = (contact, user) =>
  `mailto:${contact}?subject=${encodeURIComponent("HorizonQuest — unlock the full app")}&body=${encodeURIComponent(
    `Hi Amy,\n\nI've been trying the free HorizonQuest trial and would like to unlock the full curriculum.\n\nName: ${user?.name || ""}\nSchool: ${user?.school || ""}\nEmail: ${user?.email || ""}\n\nThanks!`
  )}`;

export function ContactButton({ contact, user, className = "", label = "Contact us to unlock everything" }) {
  return (
    <a data-testid="trial-contact-btn" href={mailto(contact, user)}
      className={`inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground font-medium hq-glow-gold hover:-translate-y-0.5 transition-transform ${className}`}>
      <Mail className="w-4 h-4" /> {label}
    </a>
  );
}

export default function UpgradeCard({ user, title = "Unlock the full HorizonQuest", compact = false }) {
  const [cat, setCat] = useState(null);
  useEffect(() => { api.get("/trial/catalog").then((r) => setCat(r.data)).catch(() => setCat(null)); }, []);

  if (!cat) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <section data-testid="trial-upgrade-card" className="hq-glass rounded-2xl p-6 sm:p-8 border border-primary/40">
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
        <div className="max-w-xl">
          <p className="text-[10px] uppercase tracking-widest text-primary font-mono-data flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> Free trial</p>
          <h2 className="font-display text-3xl sm:text-4xl mt-1 tracking-tight">{title}</h2>
          <p className="text-slate-300 mt-3 text-sm leading-relaxed">
            Your trial includes <span className="text-[#34D399]">Documents Blocks 1–3 and the Mid-Unit Task Test</span> for {cat.days} days, with up to {cat.max_classes} classes.
            Here is everything your Explorers get in the full version — 4 complete units with teaching slides, auto-graded lessons, Skill Drills, Block Tasks, Checkpoints, Unit Tests and the Final Exam.
          </p>
        </div>
        <ContactButton contact={cat.contact} user={user} className="shrink-0" />
      </div>

      <div className={`grid gap-4 mt-8 ${compact ? "sm:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-4"}`}>
        {cat.tracks.map((t) => (
          <div key={t.id} data-testid={`trial-catalog-${t.id}`} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
            <h3 className="font-display text-xl">{t.name}</h3>
            <p className="text-[11px] text-muted-foreground mb-3">{t.subtitle}</p>
            <ul className="space-y-1.5">
              {t.blocks.map((b) => (
                <li key={b.index} className="flex items-start gap-2 text-sm">
                  {b.included ? <CheckCircle2 className="w-4 h-4 text-[#34D399] shrink-0 mt-0.5" /> : <Lock className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />}
                  <span className={b.included ? "text-slate-100" : "text-slate-400"}>
                    Block {b.index} <span className="text-[11px] text-muted-foreground">· {b.lessons.length} lessons · {b.drills} drills{b.task ? " · Block Task" : ""} · Checkpoint</span>
                  </span>
                </li>
              ))}
              {t.mid_tests.map((m) => (
                <li key={m.title} className="flex items-start gap-2 text-sm">
                  {m.included ? <CheckCircle2 className="w-4 h-4 text-[#34D399] shrink-0 mt-0.5" /> : <Lock className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />}
                  <span className={m.included ? "text-slate-100" : "text-slate-400"}>{m.title.split(" · ")[0]}</span>
                </li>
              ))}
              <li className="flex items-start gap-2 text-sm"><Lock className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" /><span className="text-slate-400">Unit Test</span></li>
            </ul>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mt-4 flex items-center gap-2"><Lock className="w-3.5 h-3.5" /> Also in the full version: {cat.final.title}, Journey Map quests, Explorer Rankings, Guide Reports &amp; Lesson Plan Creator across every unit.</p>
    </section>
  );
}
